import { useState } from 'react';
import { uniqueFirstByTrimmedName } from '@/utils/uniqueByTrimmedName';
import { ExpandMoreIcon, ExpandLessIcon } from '@/components/ui/Icons';
import type { ProjectContractor } from '@/api/client';
import { useAuth } from '@/contexts/useAuth';
import { ContractorsDataTable } from './ReaderContractorCellTables';
import { ExpandableVerticalSlice } from './ExpandableVerticalSlice';

export interface ContractorsCellProps {
  contractors: ProjectContractor[];
  onBudgetToggle?: (rowKey: string, nextIncluded: boolean) => void;
  budgetSaving?: boolean;
  compact?: boolean;
  searchHighlightWords?: string[];
}

export function ContractorsCell({
  contractors,
  onBudgetToggle,
  budgetSaving,
  compact,
  searchHighlightWords,
}: ContractorsCellProps) {
  const { capabilities } = useAuth();
  const hidePrices = !capabilities.prices;
  const [expanded, setExpanded] = useState(false);

  if (!contractors?.length) return <span className="projects-table__empty-value">N/A</span>;

  const listForView = compact ? uniqueFirstByTrimmedName(contractors) : contractors;
  const showExpand = listForView.length > 2;

  return (
    <div className={`projects-table__contractors-cell${compact ? ' projects-table__contractors-cell--compact' : ''}`}>
      {showExpand ? (
        <>
          <ContractorsDataTable
            contractors={listForView.slice(0, 2)}
            onBudgetToggle={onBudgetToggle}
            budgetSaving={budgetSaving}
            compact={compact}
            searchHighlightWords={searchHighlightWords}
            hidePrices={hidePrices}
          />
          <ExpandableVerticalSlice expanded={expanded}>
            <ContractorsDataTable
              contractors={listForView.slice(2)}
              onBudgetToggle={onBudgetToggle}
              budgetSaving={budgetSaving}
              compact={compact}
              omitTableHeader={!compact}
              searchHighlightWords={searchHighlightWords}
              hidePrices={hidePrices}
            />
          </ExpandableVerticalSlice>
        </>
      ) : (
        <ContractorsDataTable
          contractors={listForView}
          onBudgetToggle={onBudgetToggle}
          budgetSaving={budgetSaving}
          compact={compact}
          searchHighlightWords={searchHighlightWords}
          hidePrices={hidePrices}
        />
      )}
      {showExpand && (
        <div className="projects-table__contractors-expand">
          {expanded ? (
            <button
              type="button"
              className="projects-table__contractors-expand-btn"
              onClick={() => setExpanded(false)}
              title="Свернуть"
              aria-label="Свернуть"
            >
              <ExpandLessIcon size={18} />
            </button>
          ) : (
            <button
              type="button"
              className="projects-table__contractors-expand-btn"
              onClick={() => setExpanded(true)}
              title="Показать всех"
              aria-label="Показать всех"
            >
              <ExpandMoreIcon size={18} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

ContractorsCell.displayName = 'ContractorsCell';
